// Marketplace form controller for vehicle submissions
// Handles photo upload preview, form interactions, live preview panel, and drag-and-drop reordering

import {
  reorderArray,
  removeNewPhoto as removeNewPhotoHelper,
  removePersistedPhoto,
  addFiles as addFilesHelper,
  buildFileOrder,
  buildPersistedImageOrder,
} from '../utils/wizard_helpers'

class MarketplaceFormController {
  constructor(element) {
    this.element = element
    this.fileInput = element.querySelector('[data-marketplace-form-target="fileInput"]')
    this.photoPreview = element.querySelector('[data-marketplace-form-target="photoPreview"]')
    this.uploadArea = element.querySelector('[data-marketplace-form-target="uploadArea"]')
    this.photoCount = element.querySelector('[data-marketplace-form-target="photoCount"]')

    // Form field targets
    this.yearSelect = element.querySelector('[data-marketplace-form-target="yearSelect"]')
    this.brandInput = element.querySelector('[data-marketplace-form-target="brandInput"]')
    this.modelInput = element.querySelector('[data-marketplace-form-target="modelInput"]')
    this.mileageInput = element.querySelector('[data-marketplace-form-target="mileageInput"]')
    this.locationSelect = element.querySelector('[data-marketplace-form-target="locationSelect"]')
    this.descriptionInput = element.querySelector('[data-marketplace-form-target="descriptionInput"]')
    this.priceInput = element.querySelector('[data-marketplace-form-target="priceInput"]')
    this.conditionSelect = element.querySelector('[data-marketplace-form-target="conditionSelect"]')
    this.fuelSelect = element.querySelector('[data-marketplace-form-target="fuelSelect"]')
    this.transmissionSelect = element.querySelector('[data-marketplace-form-target="transmissionSelect"]')
    this.hasDebtCheckbox = element.querySelector('[data-marketplace-form-target="hasDebtCheckbox"]')
    this.debtAmountInput = element.querySelector('[data-marketplace-form-target="debtAmountInput"]')

    // Submit button target
    this.submitButton = element.querySelector('[data-marketplace-form-target="submitButton"]')

    // Track uploaded files for add/remove
    this.selectedFiles = []
    // Track which persisted images have been removed
    this.removedPersistedIds = []
    // Ordered photo list: source of truth for rendering order
    // Each entry: { type: 'persisted', id, element } or { type: 'new', fileIndex }
    this.orderedPhotos = []

    // Drag state
    this.dragIndex = null
    this.touchClone = null
    this.touchStartIndex = null

    this.init()
  }

  init() {
    this.priceField = this.element.querySelector('[data-marketplace-form-target="priceField"]')
    this.reserveSelect = this.element.querySelector('[data-marketplace-form-target="reserveSelect"]')

    // Initialize orderedPhotos from persisted images
    if (this.photoPreview) {
      const persistedThumbs = this.photoPreview.querySelectorAll('[data-persisted-image-id]')
      persistedThumbs.forEach(thumb => {
        this.orderedPhotos.push({
          type: 'persisted',
          id: thumb.getAttribute('data-persisted-image-id'),
          element: thumb
        })
        thumb.setAttribute('draggable', 'true')
      })
    }

    this.persistedCount = this.orderedPhotos.length

    this.debtAmountField = this.element.querySelector('[data-marketplace-form-target="debtAmountField"]')

    this.bindEvents()
    this.initReserveState()
    this.initDebtState()

    // If persisted images exist, switch to grid mode
    if (this.persistedCount > 0) {
      this.showGridMode()
    }
  }

  initReserveState() {
    if (!this.priceField || !this.reserveSelect) return

    if (this.reserveSelect.value === '1') {
      this.priceField.style.display = 'none'
    } else {
      this.priceField.style.display = ''
    }
  }

  toggleReserve() {
    if (!this.priceField || !this.reserveSelect) return

    if (this.reserveSelect.value === '1') {
      this.priceField.style.display = 'none'
      const priceInput = this.priceField.querySelector('input[type="number"]')
      if (priceInput) priceInput.value = ''
    } else {
      this.priceField.style.display = ''
    }
    this.validateForm()
  }

  initDebtState() {
    if (!this.debtAmountField || !this.hasDebtCheckbox) return

    if (this.hasDebtCheckbox.checked) {
      this.debtAmountField.style.display = ''
    } else {
      this.debtAmountField.style.display = 'none'
    }
  }

  toggleDebtAmount() {
    if (!this.debtAmountField || !this.hasDebtCheckbox) return

    if (this.hasDebtCheckbox.checked) {
      this.debtAmountField.style.display = ''
    } else {
      this.debtAmountField.style.display = 'none'
      if (this.debtAmountInput) this.debtAmountInput.value = ''
    }
    this.validateForm()
  }

  bindEvents() {
    if (this.fileInput) {
      this.fileInput.addEventListener('change', () => this.previewPhotos())
    }

    if (this.reserveSelect) {
      this.reserveSelect.addEventListener('change', () => this.toggleReserve())
    }

    // Drag and drop on the large upload area (file drops)
    if (this.uploadArea) {
      this.uploadArea.addEventListener('dragover', (e) => {
        e.preventDefault()
        this.uploadArea.classList.add('file-upload--dragover')
      })

      this.uploadArea.addEventListener('dragleave', () => {
        this.uploadArea.classList.remove('file-upload--dragover')
      })

      this.uploadArea.addEventListener('drop', (e) => {
        e.preventDefault()
        this.uploadArea.classList.remove('file-upload--dragover')
        if (e.dataTransfer.files.length) {
          this.addFiles(Array.from(e.dataTransfer.files))
        }
      })
    }

    // Event delegation on photoPreview for remove buttons and drag-and-drop reordering
    if (this.photoPreview) {
      // Remove button clicks
      this.photoPreview.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('.marketplace-form__photo-remove')
        if (!removeBtn) return

        const thumb = removeBtn.closest('.marketplace-form__photo-thumb')
        const persistedId = thumb ? thumb.getAttribute('data-persisted-image-id') : null
        const newIndex = removeBtn.getAttribute('data-new-index')

        if (persistedId) {
          this.removePersistedImage(persistedId, thumb)
        } else if (newIndex !== null) {
          this.removeNewPhoto(parseInt(newIndex, 10))
        }
      })

      // Drag-and-drop reordering events
      this.photoPreview.addEventListener('dragstart', (e) => this.onDragStart(e))
      this.photoPreview.addEventListener('dragover', (e) => this.onDragOver(e))
      this.photoPreview.addEventListener('dragenter', (e) => this.onDragEnter(e))
      this.photoPreview.addEventListener('dragleave', (e) => this.onDragLeave(e))
      this.photoPreview.addEventListener('drop', (e) => this.onDrop(e))
      this.photoPreview.addEventListener('dragend', (e) => this.onDragEnd(e))

      // Touch support for mobile drag-and-drop
      this.photoPreview.addEventListener('touchstart', (e) => this.onTouchStart(e), { passive: false })
      this.photoPreview.addEventListener('touchmove', (e) => this.onTouchMove(e), { passive: false })
      this.photoPreview.addEventListener('touchend', (e) => this.onTouchEnd(e))
    }

    if (this.hasDebtCheckbox) {
      this.hasDebtCheckbox.addEventListener('change', () => this.toggleDebtAmount())
    }

    // Rebuild file input on form submit
    const form = this.element.querySelector('form')
    if (form) {
      form.addEventListener('submit', () => {
        this.rebuildFileInput()
        this.injectImageOrder()
      })
    }
  }

  // --- Drag-and-drop reordering (desktop) ---

  onDragStart(e) {
    const thumb = e.target.closest('.marketplace-form__photo-thumb')
    if (!thumb) return

    const index = this.getOrderIndex(thumb)
    if (index === -1) return

    this.dragIndex = index
    thumb.classList.add('marketplace-form__photo-thumb--dragging')
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', index.toString())
  }

  onDragOver(e) {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  onDragEnter(e) {
    e.preventDefault()
    const thumb = e.target.closest('.marketplace-form__photo-thumb')
    if (!thumb) return
    thumb.classList.add('marketplace-form__photo-thumb--drag-over')
  }

  onDragLeave(e) {
    const thumb = e.target.closest('.marketplace-form__photo-thumb')
    if (!thumb) return
    thumb.classList.remove('marketplace-form__photo-thumb--drag-over')
  }

  onDrop(e) {
    e.preventDefault()
    const thumb = e.target.closest('.marketplace-form__photo-thumb')
    if (!thumb) return
    thumb.classList.remove('marketplace-form__photo-thumb--drag-over')

    const targetIndex = this.getOrderIndex(thumb)
    if (targetIndex === -1 || this.dragIndex === null || this.dragIndex === targetIndex) return

    this.reorderPhotos(this.dragIndex, targetIndex)
  }

  onDragEnd(e) {
    this.dragIndex = null
    // Clean up all drag classes
    if (this.photoPreview) {
      this.photoPreview.querySelectorAll('.marketplace-form__photo-thumb').forEach(t => {
        t.classList.remove('marketplace-form__photo-thumb--dragging', 'marketplace-form__photo-thumb--drag-over')
      })
    }
  }

  // --- Touch support for mobile ---

  onTouchStart(e) {
    const thumb = e.target.closest('.marketplace-form__photo-thumb')
    if (!thumb) return

    // Don't interfere with remove button taps
    if (e.target.closest('.marketplace-form__photo-remove')) return

    const index = this.getOrderIndex(thumb)
    if (index === -1) return

    this.touchStartIndex = index
    this.touchStartTime = Date.now()
    this.touchStartPos = { x: e.touches[0].clientX, y: e.touches[0].clientY }
    this.touchMoved = false
  }

  onTouchMove(e) {
    if (this.touchStartIndex === null) return

    const touch = e.touches[0]
    const dx = touch.clientX - this.touchStartPos.x
    const dy = touch.clientY - this.touchStartPos.y

    // Need a minimum drag distance to start
    if (!this.touchMoved && Math.sqrt(dx * dx + dy * dy) < 10) return

    e.preventDefault()
    this.touchMoved = true

    // Create clone on first move
    if (!this.touchClone) {
      const thumbs = this.photoPreview.querySelectorAll('.marketplace-form__photo-thumb')
      const sourceThumb = thumbs[this.touchStartIndex]
      if (!sourceThumb) return

      sourceThumb.classList.add('marketplace-form__photo-thumb--dragging')

      this.touchClone = sourceThumb.cloneNode(true)
      this.touchClone.classList.add('marketplace-form__photo-thumb--touch-clone')
      this.touchClone.classList.remove('marketplace-form__photo-thumb--dragging')
      const rect = sourceThumb.getBoundingClientRect()
      this.touchClone.style.width = rect.width + 'px'
      this.touchClone.style.height = rect.height + 'px'
      document.body.appendChild(this.touchClone)
    }

    // Move clone to follow finger
    this.touchClone.style.left = (touch.clientX - this.touchClone.offsetWidth / 2) + 'px'
    this.touchClone.style.top = (touch.clientY - this.touchClone.offsetHeight / 2) + 'px'

    // Determine drop target
    this.touchClone.style.display = 'none'
    const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY)
    this.touchClone.style.display = ''

    // Clear previous drag-over highlights
    this.photoPreview.querySelectorAll('.marketplace-form__photo-thumb--drag-over').forEach(t => {
      t.classList.remove('marketplace-form__photo-thumb--drag-over')
    })

    if (elementBelow) {
      const targetThumb = elementBelow.closest('.marketplace-form__photo-thumb')
      if (targetThumb && targetThumb !== this.touchClone) {
        targetThumb.classList.add('marketplace-form__photo-thumb--drag-over')
      }
    }
  }

  onTouchEnd(e) {
    if (this.touchStartIndex === null) return

    // Clean up clone
    if (this.touchClone) {
      this.touchClone.remove()
      this.touchClone = null
    }

    // Clean up classes
    if (this.photoPreview) {
      this.photoPreview.querySelectorAll('.marketplace-form__photo-thumb').forEach(t => {
        t.classList.remove('marketplace-form__photo-thumb--dragging', 'marketplace-form__photo-thumb--drag-over')
      })
    }

    if (this.touchMoved) {
      // Find target from last touch position
      const touch = e.changedTouches[0]
      const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY)
      if (elementBelow) {
        const targetThumb = elementBelow.closest('.marketplace-form__photo-thumb')
        if (targetThumb) {
          const targetIndex = this.getOrderIndex(targetThumb)
          if (targetIndex !== -1 && targetIndex !== this.touchStartIndex) {
            this.reorderPhotos(this.touchStartIndex, targetIndex)
          }
        }
      }
    }

    this.touchStartIndex = null
    this.touchMoved = false
  }

  // --- Reorder helpers ---

  getOrderIndex(thumbElement) {
    const orderAttr = thumbElement.getAttribute('data-order-index')
    if (orderAttr !== null) return parseInt(orderAttr, 10)
    return -1
  }

  reorderPhotos(fromIndex, toIndex) {
    this.orderedPhotos = reorderArray(this.orderedPhotos, fromIndex, toIndex)
    this.renderGrid()
    this.ensureCompactUploadButton()
  }

  // --- Photo count and mode ---

  get totalPhotoCount() {
    return this.orderedPhotos.length
  }

  get activePersistedCount() {
    return this.orderedPhotos.filter(p => p.type === 'persisted').length
  }

  updatePhotoCounter() {
    if (this.photoCount) {
      this.photoCount.textContent = this.totalPhotoCount
    }
  }

  showGridMode() {
    if (this.uploadArea) this.uploadArea.style.display = 'none'
    if (this.photoPreview) this.photoPreview.style.display = 'grid'
    this.ensureCompactUploadButton()
  }

  showDropzoneMode() {
    if (this.uploadArea) this.uploadArea.style.display = ''
    if (this.photoPreview) this.photoPreview.style.display = 'none'
  }

  ensureCompactUploadButton() {
    if (!this.photoPreview) return

    // Remove existing compact button if present
    const existing = this.photoPreview.querySelector('.file-upload--compact')
    if (existing) existing.remove()

    // Create compact add-photo label
    const compactLabel = document.createElement('label')
    compactLabel.className = 'file-upload--compact cursor-pointer'
    compactLabel.innerHTML = `
      <svg width="24" height="24" stroke="currentColor" fill="none" viewBox="0 0 48 48">
        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
      <span class="file-upload--compact__text">Agregar foto</span>
    `
    compactLabel.addEventListener('click', (e) => {
      e.preventDefault()
      if (this.fileInput) this.fileInput.click()
    })

    this.photoPreview.appendChild(compactLabel)
  }

  addFiles(newFiles) {
    const result = addFilesHelper(this.orderedPhotos, this.selectedFiles, newFiles)
    if (result.added.length === 0) return

    this.orderedPhotos = result.ordered
    this.selectedFiles = result.selected

    this.renderGrid()
    this.showGridMode()
    this.updatePhotoCounter()
    this.validateForm()
  }

  previewPhotos() {
    if (!this.fileInput) return

    const files = Array.from(this.fileInput.files)
    if (files.length === 0) return

    this.addFiles(files)
  }

  removeNewPhoto(fileIndex) {
    const result = removeNewPhotoHelper(this.orderedPhotos, this.selectedFiles, fileIndex)
    this.orderedPhotos = result.ordered
    this.selectedFiles = result.selected

    if (this.totalPhotoCount === 0) {
      this.showDropzoneMode()
    } else {
      this.renderGrid()
      this.ensureCompactUploadButton()
    }

    this.updatePhotoCounter()
    this.validateForm()
  }

  removePersistedImage(imageId, thumbElement) {
    this.removedPersistedIds.push(imageId)

    this.orderedPhotos = removePersistedPhoto(this.orderedPhotos, imageId)

    // Add a hidden field to tell the server to remove this image
    const form = this.element.querySelector('form')
    if (form) {
      const hidden = document.createElement('input')
      hidden.type = 'hidden'
      hidden.name = 'remove_image_ids[]'
      hidden.value = imageId
      form.appendChild(hidden)
    }

    // Remove the thumbnail from the DOM
    if (thumbElement) thumbElement.remove()

    if (this.totalPhotoCount === 0) {
      this.showDropzoneMode()
    } else {
      this.renderGrid()
      this.ensureCompactUploadButton()
    }

    this.updatePhotoCounter()
    this.validateForm()
  }

  renderGrid() {
    if (!this.photoPreview) return

    // Remove all thumbs and the compact button
    const allThumbs = this.photoPreview.querySelectorAll('.marketplace-form__photo-thumb')
    allThumbs.forEach(t => t.remove())
    const compactBtn = this.photoPreview.querySelector('.file-upload--compact')
    if (compactBtn) compactBtn.remove()

    // Render all photos in orderedPhotos order
    this.orderedPhotos.forEach((entry, orderIndex) => {
      if (entry.type === 'persisted') {
        // Re-use the persisted element
        const thumb = entry.element
        thumb.setAttribute('data-order-index', orderIndex)
        thumb.setAttribute('draggable', 'true')
        this.photoPreview.appendChild(thumb)
      } else {
        // Create new file thumbnail
        const file = this.selectedFiles[entry.fileIndex]
        if (!file) return

        const thumb = document.createElement('div')
        thumb.className = 'marketplace-form__photo-thumb'
        thumb.setAttribute('data-order-index', orderIndex)
        thumb.setAttribute('draggable', 'true')

        const removeBtn = document.createElement('button')
        removeBtn.type = 'button'
        removeBtn.className = 'marketplace-form__photo-remove'
        removeBtn.setAttribute('data-new-index', entry.fileIndex)
        removeBtn.setAttribute('aria-label', 'Eliminar foto')
        removeBtn.textContent = '\u00D7'

        const img = document.createElement('img')
        img.className = 'w-full h-full object-cover'
        img.alt = `Preview ${orderIndex + 1}`

        const reader = new FileReader()
        reader.onload = (e) => {
          img.src = e.target.result
        }
        reader.readAsDataURL(file)

        thumb.appendChild(img)
        thumb.appendChild(removeBtn)
        this.photoPreview.appendChild(thumb)
      }
    })
  }

  rebuildFileInput() {
    if (!this.fileInput || this.selectedFiles.length === 0) return

    try {
      const newOrder = buildFileOrder(this.orderedPhotos, this.selectedFiles)

      const dt = new DataTransfer()
      newOrder.forEach(file => dt.items.add(file))
      this.fileInput.files = dt.files
    } catch (e) {
      // DataTransfer not supported in older browsers; files will submit as-is
    }
  }

  injectImageOrder() {
    const form = this.element.querySelector('form')
    if (!form) return

    // Remove any existing image_order fields
    form.querySelectorAll('input[name="image_order[]"]').forEach(el => el.remove())

    const ids = buildPersistedImageOrder(this.orderedPhotos)
    ids.forEach(id => {
      const hidden = document.createElement('input')
      hidden.type = 'hidden'
      hidden.name = 'image_order[]'
      hidden.value = id
      form.appendChild(hidden)
    })
  }

  validateForm() {
    if (!this.submitButton) return

    const hasPhotos = this.totalPhotoCount > 0
    const hasLocation = this.locationSelect && this.locationSelect.value !== ''
    const hasYear = this.yearSelect && this.yearSelect.value !== ''
    const hasBrand = this.brandInput && this.brandInput.value.trim() !== ''
    const hasModel = this.modelInput && this.modelInput.value.trim() !== ''
    const hasMileage = this.mileageInput && this.mileageInput.value !== ''
    const hasCondition = this.conditionSelect && this.conditionSelect.value !== ''
    const hasFuel = this.fuelSelect && this.fuelSelect.value !== ''
    const hasTransmission = this.transmissionSelect && this.transmissionSelect.value !== ''
    const hasDescription = this.descriptionInput && this.descriptionInput.value.trim() !== ''

    const needsPrice = this.reserveSelect && this.reserveSelect.value === '0'
    const hasPrice = this.priceInput && this.priceInput.value !== ''
    const priceValid = !needsPrice || hasPrice

    const allValid = hasPhotos && hasLocation && hasYear && hasBrand && hasModel &&
      hasMileage && hasCondition && hasFuel && hasTransmission && hasDescription && priceValid

    this.submitButton.disabled = !allValid
  }
}

// Auto-initialize marketplace forms
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-controller="marketplace-form"]').forEach(el => {
    new MarketplaceFormController(el)
  })
})

// Also initialize on Turbolinks load if present
document.addEventListener('turbolinks:load', () => {
  document.querySelectorAll('[data-controller="marketplace-form"]').forEach(el => {
    if (!el.marketplaceFormInitialized) {
      new MarketplaceFormController(el)
      el.marketplaceFormInitialized = true
    }
  })
})

export default MarketplaceFormController
